/**
 * Shared domain types — Xyden Leadership Assessment Platform
 * Step 6F Green Phase
 */

export type BadgeVariant = 'draft' | 'active' | 'published' | 'archived' | 'error' | 'warning' | 'pending';

export type MaturityLevel = 'L1' | 'L2' | 'L3' | 'L4' | 'M1' | 'M2' | 'M3' | 'M4';

export type VersionState = 'Draft' | 'Active' | 'Archived';

export interface PaginationMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

export interface FacultyActivationThreshold {
  points_range: string;
  faculties_activated: string;
}

export interface Version {
  id: string;
  version_name: string;
  state: VersionState;
  changelog_notes: string;
  created_by?: string;
  /** Display name resolved server-side from the User entity referenced by
   *  created_by. Populated by /api/versions list and detail endpoints so
   *  pages don't need a separate (Super-Admin-only) /api/users call. */
  created_by_name?: string | null;
  created_at?: string;
  based_on_version_id?: string | null;
  // Config fields
  faculty_levels_per_activity?: number;
  answer_option_tags?: string[];
  answer_options_per_tag?: number;
  variant_types?: string[];
  pathways?: string[];
  faculty_activation_thresholds?: FacultyActivationThreshold[];
  [key: string]: unknown;
}

export interface Activity {
  id: string;
  name: string;
  bucket_id: string;
  description?: string;
  order?: number;
  status?: string;
  version_id?: string;
}

export interface Bucket {
  id: string;
  name: string;
  description?: string;
  order?: number;
  status?: string;
  version_id?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  roles: string[];
  status?: string;
  [key: string]: unknown;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  read: boolean;
  type?: string;
  user_id?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  created_at?: string;
}

export interface TriadMapping {
  id?: string;
  activity_id: string;
  faculty_id: string;
  faculty_level?: string;
  competency_id?: string;
  competency_name?: string;
  amp_behaviour_id?: string;
  amp_technique_id?: string;
  com_behaviour_id?: string;
  com_technique_id?: string;
  version_id?: string;
}

export interface CompoundCondition {
  condition_tags: string[];
  condition_operator: string;
  condition_value: number;
}

export interface ScoringRule {
  id: string;
  priority: number;
  conditions?: Tag[];
  condition_tags: string[];
  condition_operator: string;
  condition_value: number;
  condition_logic?: 'AND' | null;
  _compound_conditions?: CompoundCondition[];
  outcome: string;
  scoring_rule_set_id?: string;
}

export interface Tag {
  label: string;
  value?: number;
}

export interface Test {
  id: string;
  version_id?: string;
  content_version_id?: string;
  scenario?: 1 | 2 | 3 | 4 | 5;
  scenario_type?: string;
  name: string;
  state?: string;
  status?: string;
  client_company_id?: string;
  due_date?: string;
  min_option_points_threshold?: number;
  [key: string]: unknown;
}

export interface Participant {
  id: string;
  test_id: string;
  user_id?: string;
  email: string;
  name?: string;
  linkedin_url?: string;
  status: string;
}

export interface ActivityResult {
  id?: string;
  activity_id: string;
  participant_id?: string;
  maturity_level?: MaturityLevel;
  activity_maturity?: string;
  faculty_maturity?: string;
  behaviour_maturity?: string;
  technique_maturity?: string;
  pathway?: 'Amplifying' | 'Compensating' | 'Baseline';
  triad_pathway?: string;
}

export interface NavItem {
  label: string;
  href?: string;
  icon?: string;
  group?: boolean;
  children?: NavItem[];
  /** When true, a group renders expanded on first paint (e.g. the multi-role "Switch module" switcher). */
  defaultExpanded?: boolean;
}

export interface Competency {
  id: string;
  name: string;
  definition?: string;
  status?: string;
  version_id?: string;
  type?: string;
}

export interface ScoringRuleSet {
  id: string;
  name: string;
  description?: string;
  status?: 'active' | 'inactive';
  version_id?: string;
}

export interface ClientCompany {
  id: string;
  name: string;
}

export interface NarrativeIndex {
  id: string;
  activity_maturity?: string;
  pathway?: string;
  summary?: string;
  strengths?: string;
  risks?: string;
  coaching_cues?: string;
  source?: string;
  status?: string;
  version_id?: string;
}

export interface Baseline {
  id: string;
  name: string;
  source_type?: string;
  status?: string;
  content_version_id?: string;
}

export interface TPAllocation {
  id?: string;
  activity_id: string;
  points: number;
  test_id?: string;
  participant_id?: string;
  source?: string;
  status?: string;
}

export interface ActivityMaturityMap {
  id: string;
  version_id?: string;
  faculty_band?: string;
  behaviour_band?: string;
  technique_band?: string;
  maturity_level?: string;
}

export interface AQQuestion {
  question_number?: number;
  total_questions?: number;
  activity_id?: string;
  question_type?: string;
  question_text?: string;
  text?: string;
  activity_name?: string;
  type?: string;
  id?: string;
  options?: Array<{
    id: string;
    text: string;
    tag: string;
  }>;
}

export interface ValidationResult {
  version_id?: string;
  blocking_errors?: Array<{ condition: string; severity?: string }>;
  warnings?: Array<{ condition: string }>;
  status: 'all_passed' | 'blocked' | 'warning';
}

export interface KPIData {
  total?: number;
  completed?: number;
  in_progress?: number;
  not_started?: number;
  completion_rate?: number;
}

// Create Test Wizard step definitions (shared across all wizard step components)
export const WIZARD_STEPS = [
  'Scenario',
  'Details',
  'Participants',
  'Configuration',
  'Review',
] as const;

// Per-triad result reconstruction (rebuilt server-side from AQResponses, not aggregated).
export type TriadCellStatus = 'answered' | 'skipped_already_served' | 'not_served' | 'score_failed';

export interface TriadCell {
  competency_id: string;
  competency_name: string | null;
  question_id: string | null;
  maturity: 'L1' | 'L2' | 'L3' | 'L4' | null;
  status: TriadCellStatus;
}

export interface TriadResultRow {
  participant_id: string;
  participant_name: string;
  participant_email: string;
  activity_id: string;
  activity_name: string;
  bucket_id: string;
  bucket_name: string;
  faculty_level: string;
  pathway: 'Amplifying' | 'Compensating' | null;
  faculty: TriadCell;
  behaviour: TriadCell;
  technique: TriadCell;
  triad_maturity: 'M1' | 'M2' | 'M3' | 'M4' | null;
}

export interface ActivityTPPoint {
  participant_id: string;
  participant_name: string;
  activity_id: string;
  activity_name: string;
  tp_points: number;
}

export interface BucketActivitySummary {
  activity_id: string;
  activity_name: string;
  order: number;
}

export interface BucketSummary {
  bucket_id: string;
  bucket_name: string;
  order: number;
  activities: BucketActivitySummary[];
}

export interface TriadResultsResponse {
  test_id: string;
  rows: TriadResultRow[];
  activity_tp_points: ActivityTPPoint[];
  buckets: BucketSummary[];
}

export type ResultsExportScope = 'summary' | 'triads' | 'responses' | 'all';

// ---------------------------------------------------------------------------
// Results dashboard payload — mirrors the GET /api/results/{testId} shape
// returned by backend/src/xyden/api/routers/results.py:get_test_results.
// ---------------------------------------------------------------------------

export interface ResultsSummary {
  overall_maturity?: string;
  pathway?: string | null;
}

export interface ComparisonPeer {
  label: string;
  overall_maturity: string;
}

export interface ComparisonData {
  enabled: boolean;
  anonymized: boolean;
  peer_data: ComparisonPeer[];
}

export interface RoleTpAlignmentActivity {
  activity_id: string;
  activity_name: string;
  role_tp_expectation: number;
  /** True when role TP for this activity crosses the activation
   * threshold, meaning the candidate was served questions for it. */
  tp_activated: boolean;
}

export interface RoleTpAlignment {
  activities: RoleTpAlignmentActivity[];
  missing_inputs?: string[];
}

/** One candidate triad gapped against the baseline's per-activity reference
 * maturity. Triads are compared individually — an activity may contribute
 * several rows, each with its own M score (no activity-level rollup). */
export interface BaselineComparisonRow {
  activity_id: string;
  activity_name: string;
  faculty_level: string | null;
  pathway: string | null;
  /** This triad's own maturity (M-scale). */
  candidate_maturity: string | null;
  /** The baseline's reference maturity for this activity (M-scale). */
  baseline_maturity: string | null;
  gap: 'at' | 'above' | 'below' | null;
}

export interface BaselineReadinessParticipant {
  participant_id: string;
  participant_name: string;
  comparison: BaselineComparisonRow[];
}

export interface BaselineReadiness {
  status: 'not_linked' | 'linked';
  baseline_id?: string | null;
  baseline_name?: string | null;
  participants: BaselineReadinessParticipant[];
  message?: string;
  version_warning?: { message?: string; blocking?: boolean } | null;
}

export interface ScoringDiagnostics {
  invalid?: number;
  partial?: number;
  dropped_dupes?: number;
  no_rule_match?: number;
}

export interface ResultsData {
  test_id?: string;
  test_name?: string;
  status?: string;
  scenario_type?: string;
  /** Result-gating envelope (FR-015). ``locked`` is the top-level flag; when
   * locked, ``status`` is "results_locked", ``lock_code`` is
   * "RESULT_GATING_LOCKED", and ``missing_inputs`` enumerates the human-readable
   * inputs still required. */
  locked?: boolean;
  lock_reason?: string | null;
  lock_code?: string | null;
  missing_inputs?: string[] | null;
  summary?: ResultsSummary | null;
  results?: {
    test_id?: string;
    summary?: ResultsSummary | null;
  } | null;
  participants?: Array<{
    participant_id: string;
    name: string;
    email: string;
    status?: string;
    overall_maturity?: string | null;
  }>;
  view_type?: string;
  comparison?: ComparisonData | null;
  role_tp_alignment?: RoleTpAlignment | null;
  baseline_readiness?: BaselineReadiness | null;
  scoring_diagnostics?: ScoringDiagnostics | null;
}
